import React from "react";
import moment from "moment-timezone";
import { Expiration } from "@lhc/commodities/dates";
import { FieldMetaMap } from "@/lib/form-v2";
import {
  ContractTypeOptions,
  CommodityOptions,
  Commodities,
  findMatchOption,
  formulateOptionMonths,
  TradingAccount,
  generateAccountLabel,
} from "@/lib/api/otc";
import { Customer } from "@/lib/api/reports";
import { Trader } from "@/lib/api/reports";
import {
  getOptionMonthComboBase,
  deriveShipmentDatesFromShortDatedOptionMonth,
} from "@/lib/api/common";
import {
  ignoreSafeParseDecimal,
  computeCanolaStepFutures,
  getStepsForContractType,
  isCanolaContractWithFuturesRows,
  ContractFuturesStep,
} from "../OtcBearState";
import { formatDateWithoutTimezone } from "@/lib/api/date";
import { OtcFormValues } from "./types";

/**
 * Serialize a Date to a string for Zod validation.
 * The Zod schema expects z.string() for date fields.
 * Uses formatDateWithoutTimezone to produce "YYYY/MM/DD" —
 * a non-empty string that satisfies z.string().
 */
function serializeDate(value: unknown): unknown {
  if (value instanceof Date) {
    return formatDateWithoutTimezone(value);
  }
  return value;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function tryComputeExpiration(optionMonth: string): Date | undefined {
  try {
    const result = getOptionMonthComboBase(optionMonth);
    return Expiration.calculateLastTradingDay(
      result.firstDayOfMonthMoment,
    );
  } catch {
    return undefined;
  }
}

function computeMargin(v: OtcFormValues): string {
  const fee = ignoreSafeParseDecimal(v.contract_fee || 0);
  const premium = ignoreSafeParseDecimal(v.option_premium || 0);
  return fee.sub(premium).toString();
}

function computeMarginAmount(v: OtcFormValues): string {
  const fee = ignoreSafeParseDecimal(v.contract_fee || 0);
  const premium = ignoreSafeParseDecimal(v.option_premium || 0);
  return fee.sub(premium).times(ignoreSafeParseDecimal(v.quantity || 0)).toString();
}

function computeAdjMargin(
  v: OtcFormValues,
  feeKey: keyof OtcFormValues,
  premiumKey: keyof OtcFormValues,
): string {
  const fee = v[feeKey];
  const premium = v[premiumKey];
  if (!fee || !premium) return "0";
  return ignoreSafeParseDecimal(fee as string | number)
    .sub(ignoreSafeParseDecimal(premium as string | number))
    .toString();
}

function computeAdjMarginAmount(
  v: OtcFormValues,
  feeKey: keyof OtcFormValues,
  premiumKey: keyof OtcFormValues,
): string {
  const fee = v[feeKey];
  const premium = v[premiumKey];
  if (!fee || !premium) return "0";
  return ignoreSafeParseDecimal(fee as string | number)
    .sub(ignoreSafeParseDecimal(premium as string | number))
    .times(ignoreSafeParseDecimal(v.quantity || 0))
    .toString();
}

/**
 * Returns true when the user has entered a non-empty value into an
 * Adj Option Premium field.
 */
function hasAdjOptionPremiumValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
}

/**
 * Build the effects fn for an Adj Option Premium field that auto-fills
 * its corresponding Adj Date with today's date when the user enters a
 * premium and the date is currently empty.
 */
function adjOptionPremiumEffects(dateKey: keyof OtcFormValues) {
  return (
    newValue: unknown,
    values: OtcFormValues,
  ): Partial<OtcFormValues> | undefined => {
    if (!hasAdjOptionPremiumValue(newValue)) return undefined;
    if (values[dateKey]) return undefined;
    return { [dateKey]: new Date() } as Partial<OtcFormValues>;
  };
}

/**
 * Effects fn for the Final Priced Amount / Final Quoted fields: when the
 * user enters a value and final_futures_price_date is currently empty,
 * default the date to today.
 */
function finalFuturesPriceEffects(
  newValue: unknown,
  values: OtcFormValues,
): Partial<OtcFormValues> | undefined {
  if (!hasAdjOptionPremiumValue(newValue)) return undefined;
  if (values.final_futures_price_date) return undefined;
  return { final_futures_price_date: new Date() };
}

/**
 * Returns true when at least one of the three paired Final Futures fields
 * (price, expected price, date) has a value other than the given exclusion.
 * Used to drive requiredOverride: any field becomes required as soon as one
 * of the other two is filled in.
 */
function anyOtherFinalFuturesFieldSet(
  v: OtcFormValues,
  exclude: "final_futures_price" | "final_futures_expected_price" | "final_futures_price_date",
): boolean {
  const isEmpty = (val: unknown): boolean =>
    val === undefined || val === null || (typeof val === "string" && val.trim() === "");
  if (exclude !== "final_futures_price" && !isEmpty(v.final_futures_price)) return true;
  if (exclude !== "final_futures_expected_price" && !isEmpty(v.final_futures_expected_price)) return true;
  // Treat a malformed Date (e.g. new Date("")) as not set. Use moment for validity check.
  if (exclude !== "final_futures_price_date" && v.final_futures_price_date != null && moment(v.final_futures_price_date).isValid()) return true;
  return false;
}

/**
 * Derive `final_futures_price_margin` for the form. Mirrors the backend
 * formula: (final_futures_expected_price - final_futures_price) * quantity.
 * Returns "" unless all three inputs are non-empty (matching the backend
 * which stores null in that case).
 */
function computeFinalFuturesPriceMargin(v: OtcFormValues): string {
  const expectedRaw = v.final_futures_expected_price;
  const priceRaw = v.final_futures_price;
  const quantityRaw = v.quantity;
  const isEmpty = (s: unknown): boolean =>
    s === undefined || s === null || (typeof s === "string" && s.trim() === "");
  if (isEmpty(expectedRaw) || isEmpty(priceRaw) || isEmpty(quantityRaw)) return "";
  const expected = ignoreSafeParseDecimal(expectedRaw as string | number);
  const price = ignoreSafeParseDecimal(priceRaw as string | number);
  const quantity = ignoreSafeParseDecimal(quantityRaw as string | number);
  const result = expected.sub(price).times(quantity);
  return result.isZero() ? "" : result.toFixed(2);
}

function stepIsActiveForRow(v: OtcFormValues, step: ContractFuturesStep): boolean {
  if (!isCanolaContractWithFuturesRows({
    commodity: v.commodity as string,
    contract_type: v.contract_type as string,
  })) {
    return false;
  }
  return getStepsForContractType(v.contract_type as string).includes(step);
}

function createOtcAccountOptions(accounts: TradingAccount[]) {
  return accounts
    .filter((a) => a.is_otc || a.is_futures)
    .map((a) => ({
      value: a.id,
      label: generateAccountLabel(a.account_id) || a.account_id,
    }));
}

function createHtaAccountOptions(accounts: TradingAccount[]) {
  return accounts
    .filter((a) => a.is_futures || false)
    .map((a) => ({
      value: a.id,
      label: generateAccountLabel(a.account_id) || a.account_id,
    }));
}

// ---------------------------------------------------------------------------
// Build the field meta map
// ---------------------------------------------------------------------------

export function buildFieldMeta(opts: {
  accounts: TradingAccount[];
  customers: Customer[];
  traders: Trader[];
  isPreviewOnly?: boolean;
}): FieldMetaMap<OtcFormValues> {
  const { accounts, customers, traders, isPreviewOnly } = opts;

  const customerOptions = customers.map((c) => ({
    value: c.id,
    label: c.long_name,
  }));
  const traderOptions = traders.map((t) => ({
    value: t.id,
    label: t.name || "",
  }));
  const otcAccountOptions = createOtcAccountOptions(accounts);
  const htaAccountOptions = createHtaAccountOptions(accounts);

  const d = isPreviewOnly ?? false;

  return {
    // ========================== Core ==========================
    date: { label: "Date", widget: "date", disabled: d, serializeForValidation: serializeDate },
    end_date: { label: "Expiration Date", widget: "date", disabled: d, serializeForValidation: serializeDate },
    contract_type: {
      label: "Contract Type",
      widget: "select",
      disabled: d,
      options: ContractTypeOptions,
      effects: (newValue, _values, _prevValues) => {
        // Clear the Final Futures row when switching to a contract type with no
        // futures steps (the row hides because isCanolaContractWithFuturesRows
        // returns false). Without clearing, hidden values would still be sent
        // to the API on submit.
        const nextType = newValue as string | undefined;
        if (getStepsForContractType(nextType).length === 0) {
          return {
            final_futures_price: undefined,
            final_futures_expected_price: undefined,
            final_futures_price_date: undefined,
            final_futures_price_margin: undefined,
          };
        }
        return undefined;
      },
    },
    customer_id: {
      label: "Customer",
      widget: "select",
      disabled: d,
      options: customerOptions,
    },
    commodity: {
      label: "Commodity",
      widget: "select",
      disabled: d,
      options: CommodityOptions,
      effects: (newValue, values, prevValues) => {
        const commodity = findMatchOption(CommodityOptions, newValue as string)?.value;
        const patch: Partial<OtcFormValues> = {};

        if (commodity && values.option_month) {
          const exp = tryComputeExpiration(values.option_month);
          if (exp) patch.end_date = exp;
        }

        if (values.option_month) {
          // Re-derive shipment dates for the new commodity. Short-dated option
          // months derive from the commodity's new-crop month; non short-dated
          // months clear the shipment dates.
          const shipment = deriveShipmentDatesFromShortDatedOptionMonth(
            commodity,
            values.option_month as string,
          );
          patch.shipment_start_date = shipment?.shipment_start_date;
          patch.shipment_end_date = shipment?.shipment_end_date;
        }

        if (prevValues.commodity === Commodities.Canola && commodity !== Commodities.Canola) {
          patch.implied_fx_amount = undefined;
          patch.actual_contract_futures = undefined;
          patch.final_futures_price = undefined;
          patch.final_futures_expected_price = undefined;
          patch.final_futures_price_date = undefined;
          patch.final_futures_price_margin = undefined;
        }

        return patch;
      },
    },

    // ========================== Option / Trade ==========================
    option_month: {
      label: "OTC Opt. Month",
      widget: "select",
      disabled: d,
      options: (v) => formulateOptionMonths(v.option_month as string | undefined),
      effects: (newValue, values) => {
        const matched = findMatchOption(
          formulateOptionMonths(values.option_month as string | undefined),
          newValue as string,
        )?.value;
        if (!matched) return {};
        const patch: Partial<OtcFormValues> = {};
        const exp = tryComputeExpiration(matched);
        if (exp) patch.end_date = exp;
        // Short-dated option months derive shipment dates from the commodity's
        // new-crop month. Non short-dated months clear the shipment dates
        // (there is no other derivation logic for them).
        const shipment = deriveShipmentDatesFromShortDatedOptionMonth(
          values.commodity as string | undefined,
          matched,
        );
        patch.shipment_start_date = shipment?.shipment_start_date;
        patch.shipment_end_date = shipment?.shipment_end_date;
        return patch;
      },
    },
    quantity: { label: "Quantity", widget: "float", step: "5000", disabled: d },
    trade_reference: { label: "Trade Reference", widget: "text", disabled: d },
    account_no_a: {
      label: "Brokerage OTC",
      widget: "select",
      disabled: d,
      options: otcAccountOptions,
    },
    has_invoice_fees: {
      label: "Invoice Fees",
      widget: "checkbox",
      disabled: d,
    },
    has_contract_adjustment: {
      label: "Contract Fee Add/Ded",
      widget: "checkbox",
      disabled: d,
    },

    // ========================== Steps ==========================
    step_one: { label: "Step 1", widget: "float", disabled: d },
    step_two: { label: "Step 2", widget: "float", disabled: d },
    step_three: { label: "Step 3", widget: "float", disabled: d },
    double_up: { label: "Double Up", widget: "float", disabled: d },

    // ========================== Canola ==========================
    implied_fx_amount: {
      label: "Implied FX Amount",
      widget: "float",
      step: "0.0001",
      disabled: d,
      visible: (v) => v.commodity === Commodities.Canola,
      // The Zod schema shape has this as .optional(), but it is enforced as
      // required for Canola via superRefine in form-schemas.ts sharedSuperRefine.
      // requiredOverride keeps the UI "Required" label in sync with that rule.
      requiredOverride: (v) => v.commodity === Commodities.Canola,
    },
    expected_contract_futures: {
      label: "Calculated Step 3 Futures",
      widget: "float",
      visible: (v) => stepIsActiveForRow(v, "step_three"),
      derive: (v) => {
        if (!stepIsActiveForRow(v, "step_three")) return "";
        const { expected_contract_futures } = computeCanolaStepFutures({
          stepValue: v.step_three,
          implied_fx_amount: v.implied_fx_amount,
          actualContractFutures: v.actual_contract_futures,
          quantity: v.quantity as unknown as number,
        });
        return expected_contract_futures.isZero() ? "" : expected_contract_futures.toFixed(6);
      },
    },
    actual_contract_futures: {
      label: "Quoted Step 3 Futures",
      widget: "float",
      disabled: d,
      visible: (v) => stepIsActiveForRow(v, "step_three"),
      // The Zod schema shape has this as .optional(), but it is enforced as
      // required for Canola via superRefine in form-schemas.ts sharedSuperRefine.
      // requiredOverride keeps the UI "Required" label in sync with that rule.
      requiredOverride: (v) => stepIsActiveForRow(v, "step_three"),
    },
    futures_margin: {
      label: "Step 3 Futures Margin",
      widget: "float",
      visible: (v) => stepIsActiveForRow(v, "step_three"),
      derive: (v) => {
        if (!stepIsActiveForRow(v, "step_three")) return "";
        const { futures_margin } = computeCanolaStepFutures({
          stepValue: v.step_three,
          implied_fx_amount: v.implied_fx_amount,
          actualContractFutures: v.actual_contract_futures,
          quantity: v.quantity as unknown as number,
        });
        return futures_margin.isZero() ? "" : futures_margin.toFixed(2);
      },
    },

    // -------- Step 1 actual/expected/futures_margin --------
    expected_contract_futures_step_one: {
      label: "Calculated Step 1 Futures",
      widget: "float",
      visible: (v) => stepIsActiveForRow(v, "step_one"),
      derive: (v) => {
        if (!stepIsActiveForRow(v, "step_one")) return "";
        const { expected_contract_futures } = computeCanolaStepFutures({
          stepValue: v.step_one,
          implied_fx_amount: v.implied_fx_amount,
          actualContractFutures: v.actual_contract_futures_step_one,
          quantity: v.quantity as unknown as number,
        });
        return expected_contract_futures.isZero() ? "" : expected_contract_futures.toFixed(6);
      },
    },
    actual_contract_futures_step_one: {
      label: "Quoted Step 1 Futures",
      widget: "float",
      disabled: d,
      visible: (v) => stepIsActiveForRow(v, "step_one"),
      requiredOverride: (v) => stepIsActiveForRow(v, "step_one"),
    },
    futures_margin_step_one: {
      label: "Step 1 Futures Margin",
      widget: "float",
      visible: (v) => stepIsActiveForRow(v, "step_one"),
      derive: (v) => {
        if (!stepIsActiveForRow(v, "step_one")) return "";
        const { futures_margin } = computeCanolaStepFutures({
          stepValue: v.step_one,
          implied_fx_amount: v.implied_fx_amount,
          actualContractFutures: v.actual_contract_futures_step_one,
          quantity: v.quantity as unknown as number,
        });
        return futures_margin.isZero() ? "" : futures_margin.toFixed(2);
      },
    },

    // -------- Step 2 actual/expected/futures_margin --------
    expected_contract_futures_step_two: {
      label: "Calculated Step 2 Futures",
      widget: "float",
      visible: (v) => stepIsActiveForRow(v, "step_two"),
      derive: (v) => {
        if (!stepIsActiveForRow(v, "step_two")) return "";
        const { expected_contract_futures } = computeCanolaStepFutures({
          stepValue: v.step_two,
          implied_fx_amount: v.implied_fx_amount,
          actualContractFutures: v.actual_contract_futures_step_two,
          quantity: v.quantity as unknown as number,
        });
        return expected_contract_futures.isZero() ? "" : expected_contract_futures.toFixed(6);
      },
    },
    actual_contract_futures_step_two: {
      label: "Quoted Step 2 Futures",
      widget: "float",
      disabled: d,
      visible: (v) => stepIsActiveForRow(v, "step_two"),
      requiredOverride: (v) => stepIsActiveForRow(v, "step_two"),
    },
    futures_margin_step_two: {
      label: "Step 2 Futures Margin",
      widget: "float",
      visible: (v) => stepIsActiveForRow(v, "step_two"),
      derive: (v) => {
        if (!stepIsActiveForRow(v, "step_two")) return "";
        const { futures_margin } = computeCanolaStepFutures({
          stepValue: v.step_two,
          implied_fx_amount: v.implied_fx_amount,
          actualContractFutures: v.actual_contract_futures_step_two,
          quantity: v.quantity as unknown as number,
        });
        return futures_margin.isZero() ? "" : futures_margin.toFixed(2);
      },
    },

    // -------- Double Up actual/expected/futures_margin --------
    expected_contract_futures_double_up: {
      label: "Calculated Double Up Futures",
      widget: "float",
      visible: (v) => stepIsActiveForRow(v, "double_up"),
      derive: (v) => {
        if (!stepIsActiveForRow(v, "double_up")) return "";
        const { expected_contract_futures } = computeCanolaStepFutures({
          stepValue: v.double_up,
          implied_fx_amount: v.implied_fx_amount,
          actualContractFutures: v.actual_contract_futures_double_up,
          quantity: v.quantity as unknown as number,
        });
        return expected_contract_futures.isZero() ? "" : expected_contract_futures.toFixed(6);
      },
    },
    actual_contract_futures_double_up: {
      label: "Quoted Double Up Futures",
      widget: "float",
      disabled: d,
      visible: (v) => stepIsActiveForRow(v, "double_up"),
      requiredOverride: (v) => stepIsActiveForRow(v, "double_up"),
    },
    futures_margin_double_up: {
      label: "Double Up Futures Margin",
      widget: "float",
      visible: (v) => stepIsActiveForRow(v, "double_up"),
      derive: (v) => {
        if (!stepIsActiveForRow(v, "double_up")) return "";
        const { futures_margin } = computeCanolaStepFutures({
          stepValue: v.double_up,
          implied_fx_amount: v.implied_fx_amount,
          actualContractFutures: v.actual_contract_futures_double_up,
          quantity: v.quantity as unknown as number,
        });
        return futures_margin.isZero() ? "" : futures_margin.toFixed(2);
      },
    },

    // -------- Final futures price row (Canola, any contract type with steps) --------
    final_futures_expected_price: {
      label: "Final Priced Amount",
      widget: "float",
      disabled: d,
      visible: (v) => isCanolaContractWithFuturesRows({
        commodity: v.commodity as string | undefined,
        contract_type: v.contract_type as string | undefined,
      }),
      effects: finalFuturesPriceEffects,
      requiredOverride: (v) => anyOtherFinalFuturesFieldSet(v, "final_futures_expected_price"),
    },
    final_futures_price: {
      label: "Final Quoted",
      widget: "float",
      disabled: d,
      visible: (v) => isCanolaContractWithFuturesRows({
        commodity: v.commodity as string | undefined,
        contract_type: v.contract_type as string | undefined,
      }),
      effects: finalFuturesPriceEffects,
      requiredOverride: (v) => anyOtherFinalFuturesFieldSet(v, "final_futures_price"),
    },
    final_futures_price_date: {
      label: "Final Price Date",
      widget: "date",
      disabled: d,
      serializeForValidation: serializeDate,
      visible: (v) => isCanolaContractWithFuturesRows({
        commodity: v.commodity as string | undefined,
        contract_type: v.contract_type as string | undefined,
      }),
      requiredOverride: (v) => anyOtherFinalFuturesFieldSet(v, "final_futures_price_date"),
    },
    final_futures_price_margin: {
      label: "Final Futures Margin",
      widget: "float",
      disabled: true,
      visible: (v) => isCanolaContractWithFuturesRows({
        commodity: v.commodity as string | undefined,
        contract_type: v.contract_type as string | undefined,
      }),
      derive: computeFinalFuturesPriceMargin,
    },

    // ========================== Premium Offer WDU ==========================
    account_no_b: {
      label: "Brokerage HTA",
      widget: "select",
      disabled: d,
      options: htaAccountOptions,
    },
    futures_price: { label: "Futures Price", widget: "float", disabled: d },
    otc_futures: { label: "OTC Futures", widget: "float", disabled: d },

    // ========================== Financials ==========================
    option_premium: {
      label: "Option Premium",
      widget: "float",
      disabled: d,
      tooltip: {
        content: (
          <div className="space-y-1">
            <p className="text-sm">• Option premium is (+) if you paid funds for option</p>
            <p className="text-sm">• Option Premium is (-) If you collected funds for option</p>
          </div>
        ),
      },
    },
    contract_fee: { label: "Initial Premium + Fee", widget: "float", disabled: d },
    margin: {
      label: "Lighthouse Fee",
      widget: "float",
      derive: computeMargin,
    },
    margin_amount: {
      label: "Margin Amount",
      widget: "float",
      derive: computeMarginAmount,
    },
    contract_no: { label: "Contract #", widget: "text", disabled: d },
    contract_reference: { label: "Contract Reference", widget: "text", disabled: d },

    // ========================== Adjusted 1 ==========================
    adj_option_premium: {
      label: "Adj Option Premium",
      widget: "float",
      disabled: d,
      effects: adjOptionPremiumEffects("last_adjusted_date"),
    },
    adj_contract_fee: { label: "Adj Initial Premium + Fee", widget: "float", disabled: d },
    adj_margin: {
      label: "Adj Lighthouse Fee",
      widget: "float",
      derive: (v) => computeAdjMargin(v, "adj_contract_fee", "adj_option_premium"),
    },
    adj_margin_amount: {
      label: "Adj Margin Amount",
      widget: "float",
      derive: (v) => computeAdjMarginAmount(v, "adj_contract_fee", "adj_option_premium"),
    },
    last_adjusted_date: {
      label: "Adjusted Date",
      widget: "date",
      disabled: d,
      serializeForValidation: serializeDate,
      // Required when adj_option_premium has a value. Enforced via
      // superRefine in form-schemas.ts; this keeps the UI label in sync.
      requiredOverride: (v) => hasAdjOptionPremiumValue(v.adj_option_premium),
    },

    // ========================== Adjusted 2 ==========================
    adj_option_premium_two: {
      label: "Adj Option Premium 2",
      widget: "float",
      disabled: d,
      effects: adjOptionPremiumEffects("adjusted_date_two"),
    },
    adj_contract_fee_two: { label: "Adj Initial Premium + Fee 2", widget: "float", disabled: d },
    adj_margin_two: {
      label: "Adj Lighthouse Fee 2",
      widget: "float",
      derive: (v) => computeAdjMargin(v, "adj_contract_fee_two", "adj_option_premium_two"),
    },
    adj_margin_amount_two: {
      label: "Adj Margin Amount 2",
      widget: "float",
      derive: (v) => computeAdjMarginAmount(v, "adj_contract_fee_two", "adj_option_premium_two"),
    },
    adjusted_date_two: {
      label: "Adjusted Date 2",
      widget: "date",
      disabled: d,
      serializeForValidation: serializeDate,
      // Required when adj_option_premium_two has a value. Enforced via
      // superRefine in form-schemas.ts; this keeps the UI label in sync.
      requiredOverride: (v) => hasAdjOptionPremiumValue(v.adj_option_premium_two),
    },

    // ========================== Adjusted 3 ==========================
    adj_option_premium_three: {
      label: "Adj Option Premium 3",
      widget: "float",
      disabled: d,
      effects: adjOptionPremiumEffects("adjusted_date_three"),
    },
    adj_contract_fee_three: { label: "Adj Initial Premium + Fee 3", widget: "float", disabled: d },
    adj_margin_three: {
      label: "Adj Lighthouse Fee 3",
      widget: "float",
      derive: (v) => computeAdjMargin(v, "adj_contract_fee_three", "adj_option_premium_three"),
    },
    adj_margin_amount_three: {
      label: "Adj Margin Amount 3",
      widget: "float",
      derive: (v) => computeAdjMarginAmount(v, "adj_contract_fee_three", "adj_option_premium_three"),
    },
    adjusted_date_three: {
      label: "Adjusted Date 3",
      widget: "date",
      disabled: d,
      serializeForValidation: serializeDate,
      // Required when adj_option_premium_three has a value. Enforced via
      // superRefine in form-schemas.ts; this keeps the UI label in sync.
      requiredOverride: (v) => hasAdjOptionPremiumValue(v.adj_option_premium_three),
    },

    // ========================== Adjusted 4 ==========================
    adj_option_premium_four: {
      label: "Adj Option Premium 4",
      widget: "float",
      disabled: d,
      effects: adjOptionPremiumEffects("adjusted_date_four"),
    },
    adj_contract_fee_four: { label: "Adj Initial Premium + Fee 4", widget: "float", disabled: d },
    adj_margin_four: {
      label: "Adj Lighthouse Fee 4",
      widget: "float",
      derive: (v) => computeAdjMargin(v, "adj_contract_fee_four", "adj_option_premium_four"),
    },
    adj_margin_amount_four: {
      label: "Adj Margin Amount 4",
      widget: "float",
      derive: (v) => computeAdjMarginAmount(v, "adj_contract_fee_four", "adj_option_premium_four"),
    },
    adjusted_date_four: {
      label: "Adjusted Date 4",
      widget: "date",
      disabled: d,
      serializeForValidation: serializeDate,
      requiredOverride: (v) => hasAdjOptionPremiumValue(v.adj_option_premium_four),
    },

    // ========================== Adjusted 5 ==========================
    adj_option_premium_five: {
      label: "Adj Option Premium 5",
      widget: "float",
      disabled: d,
      effects: adjOptionPremiumEffects("adjusted_date_five"),
    },
    adj_contract_fee_five: { label: "Adj Initial Premium + Fee 5", widget: "float", disabled: d },
    adj_margin_five: {
      label: "Adj Lighthouse Fee 5",
      widget: "float",
      derive: (v) => computeAdjMargin(v, "adj_contract_fee_five", "adj_option_premium_five"),
    },
    adj_margin_amount_five: {
      label: "Adj Margin Amount 5",
      widget: "float",
      derive: (v) => computeAdjMarginAmount(v, "adj_contract_fee_five", "adj_option_premium_five"),
    },
    adjusted_date_five: {
      label: "Adjusted Date 5",
      widget: "date",
      disabled: d,
      serializeForValidation: serializeDate,
      requiredOverride: (v) => hasAdjOptionPremiumValue(v.adj_option_premium_five),
    },

    // ========================== Accumulator ==========================
    start_date: { label: "Start Date", widget: "date", disabled: d, serializeForValidation: serializeDate },
    weeks: { label: "Weeks", widget: "float", disabled: d },
    period_qty: { label: "Period Qty", widget: "float", disabled: d },
    knockout: { label: "Knockout", widget: "float", disabled: d },

    // ========================== Shipment / Trader ==========================
    shipment_start_date: { label: "Shipment Start Date", widget: "date", disabled: d, serializeForValidation: serializeDate },
    shipment_end_date: { label: "Shipment End Date", widget: "date", disabled: d, serializeForValidation: serializeDate },
    trader_id: {
      label: "Trader",
      widget: "select",
      disabled: d,
      options: traderOptions,
    },
    crop_year: { label: "Crop Year", widget: "int", step: "1", disabled: d },
    rolled_option_month: {
      label: "Futures Rolled Month",
      widget: "select",
      disabled: d,
      options: (v) => formulateOptionMonths(v.rolled_option_month as string | undefined),
    },

    // ========================== Carry / Basis / Notes ==========================
    carry: { label: "Futures Carry", widget: "float", disabled: d },
    rolled_basis_month: {
      label: "Basis Rolled Month",
      widget: "select",
      disabled: d,
      options: (v) => formulateOptionMonths(v.rolled_basis_month as string | undefined),
    },
    basis_carry: { label: "Basis Carry", widget: "float", disabled: d },
    notes: { label: "Notes", widget: "text", disabled: d },
    option_month_etc_one: {
      label: "HTA DU Opt. Month",
      widget: "select",
      disabled: d,
      options: (v) => formulateOptionMonths(v.option_month_etc_one as string | undefined),
    },
  };
}
